import React, { useCallback, useState } from 'react';
import { NextPageContext } from 'next';

import {
  Activities,
  Amenities,
  Breadcrumbs,
  ContactButtons,
  CtaButtons,
  EmailButton,
  FAQs,
  Floorplans,
  Headings,
  LocationsInSection,
  PhoneButton,
  PhotoModal,
  ServicesGlobals,
  TourTimes,
  Units,
} from '../../../../components';
import Image from '../../../../components/layout/Image';
import { useSiteQuery } from '../../../../hooks';
import { getLocationTitle } from '../../../../lib/FunctionLib';
import ErrorPage from '../../../_error';
import NotFoundPage from '../../../404';

import query from './index.graphql';

import styles from './index.module.scss';

interface Props {
  slug: string;
}

const RetirementVillagesLocationsPage = ({ slug }: Props) => {
  const [{ data, error }] = useSiteQuery<VillageLocationsDetailQuery>({ query, variables: { slug } });
  const [photoModalOpen, openPhotoModal] = useState(false);

  const togglePhotoModal = useCallback(() => {
    const toSet = !photoModalOpen;
    openPhotoModal(toSet);
  }, [photoModalOpen]);

  function onGalleryClick(e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) {
    if (e.type !== 'keypress' || (e as React.KeyboardEvent).key === 'Enter') {
      togglePhotoModal();
    }
  }

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  const entry = data?.entry;
  if (entry) {
    const title = getLocationTitle(entry);
    const photos = entry.photos?.filter((p): p is AssetFragment => p !== null && p !== undefined) || [];
    const floorplans = entry.floorplans || [];
    const image = entry.image?.[0];

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Retirement Villages', uri: '/retirement-villages' },
      { title: 'Retirement Village Locations', uri: '/retirement-villages/locations' },
      { title, uri: `/${entry.uri!}` },
    ];
    const activities = entry.activities || [];

    return (
      <>
        <Breadcrumbs breadcrumbs={breadcrumbs} additionalClasses={'override-bg-blue'} />
        <Headings
          title={title}
          headline={entry.headline!}
          photos={photos}
          onGalleryClick={onGalleryClick}
          location={entry.id!}
          activities={activities.length > 0}
          additionalClasses={'override-bg-blue-signature'}
        />
        <LocationsInSection customTitle="Explore our other villages" current={entry} all={data?.allLocations} />
        <Units location={entry.id!} />
        <div className="section pt-4">
          <div className="container">
            <div className="row">
              {floorplans.length === 0 && image && (
                <div className="col-lg-6">
                  <div className={styles.overviewImage}>
                    <Image {...image} alt={title} />
                  </div>
                </div>
              )}
              <div className="col-lg-6">
                <div id="village-overview" className="sectionHeading">
                  Village Overview
                </div>
                <div dangerouslySetInnerHTML={{ __html: entry.body! }} />
                <div className="desktopOnly">
                  {(floorplans.length > 0 || image) && (
                    <>
                      <p>
                        <strong>Get in touch for more details</strong>
                      </p>
                      <ContactButtons
                        customPhoneNumber={entry.phone!}
                        customEmailDesktop={<EmailButton section="Retirement Village Location" name={entry.title!} />}
                        desktop
                      />
                    </>
                  )}
                  {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                </div>
              </div>
              {(floorplans.length > 0 || image) && (
                <div className="col-lg-6">
                  {floorplans.length > 0 && <Floorplans floorplans={floorplans} />}
                  <div className="mobileOnly">
                    <p>
                      <strong>Get in touch for more details</strong>
                    </p>
                    <ContactButtons
                      customPhoneNumber={entry.phone!}
                      customEmailMobile={
                        <EmailButton section="Retirement Village Location" name={entry.title!} text="Email us" />
                      }
                      mobile
                    />
                    {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                  </div>
                </div>
              )}
              {floorplans.length === 0 && !image && (
                <div className="col-lg-6">
                  <div className="mobileOnly">
                    {entry.tourTimes && <TourTimes time={entry.tourTimes} className={styles.tourTimes} />}
                  </div>
                  <CtaButtons
                    headline="Get in touch for more details about this property"
                    description="Send an email or give us a call during business hours"
                  >
                    <div>
                      <EmailButton type="orangeBorder" section="Retirement Village Location" name={entry.title!} />
                    </div>
                    <div>
                      <PhoneButton customPhone={entry.phone!} />
                    </div>
                  </CtaButtons>
                </div>
              )}
            </div>
          </div>
        </div>
        {entry.activities! && <Activities activities={entry.activities} />}
        <ServicesGlobals />
        <Amenities title={entry.title!} address={entry.address!} amenities={entry.amenities} />
        <FAQs topic="retirement-villages" />
        {photoModalOpen && photos && <PhotoModal photos={photos} handleClose={togglePhotoModal} />}
      </>
    );
  }
  return <NotFoundPage />;
};

RetirementVillagesLocationsPage.getInitialProps = ({ req }: NextPageContext) => ({
  slug: req?.url?.substring(req?.url?.lastIndexOf('/') + 1).replace(/\?.*$/, ''),
});

export default RetirementVillagesLocationsPage;
